/**
 * The decisions behind `v-drag-scroll` (directives/dragScroll.js): when a press
 * on a grid may become a pan, and when a pan has really started.
 *
 * Panning is held behind Alt (⌥ on a Mac). A bare drag over the rows is how
 * people select text — an e-mail address, a phone number — and taking it over
 * cost them that; with Alt held, the drag moves the grid instead.
 *
 * Even then, a press on a row is first a click — it opens the record. It turns
 * into a pan only once the pointer has travelled past the threshold; from then
 * on, the click that the browser fires on release is the end of a gesture, not
 * a request to open anything.
 *
 * Listed in docs/raccourcis-clavier.md, with every other shortcut.
 */

/** How far, in pixels, the pointer travels before a press becomes a pan. */
export const PAN_THRESHOLD = 5;

/**
 * Everything that already answers a press on its own: typing, ticking, opening
 * a menu, following a link, dragging a column's edge. A pan never starts there.
 */
export const INTERACTIVE_SELECTOR = [
    'input',
    'textarea',
    'select',
    'button',
    'a',
    'label',
    '[contenteditable]',
    '[role="button"]',
    '.checkbox',
    '.dropdown',
    '.col-resize-grip',
    '.grid-col-manager'
].join(',');

/**
 * Whether the modifiers held are exactly the pan's: Alt alone. Shift, Ctrl and
 * Cmd keep their own meaning, alone or combined with Alt.
 *
 * @param {{ shiftKey?: boolean, ctrlKey?: boolean, metaKey?: boolean, altKey?: boolean }} keys
 */
export function isPanModifier(keys) {
    return !!keys.altKey && !keys.shiftKey && !keys.ctrlKey && !keys.metaKey;
}

/**
 * Whether a press may start a pan: the main mouse button, Alt held alone, on
 * nothing interactive.
 *
 * @param {{ button: number, shiftKey?: boolean, ctrlKey?: boolean, metaKey?: boolean, altKey?: boolean, target: { closest?: Function } | null }} press
 */
export function canStartPan(press) {
    if (press.button !== 0 || !isPanModifier(press)) {
        return false;
    }

    return !press.target?.closest?.(INTERACTIVE_SELECTOR);
}

/**
 * Whether the pointer has travelled far enough from where it was pressed for
 * the press to be a pan rather than a click.
 */
export function passedThreshold(dx, dy, threshold = PAN_THRESHOLD) {
    return Math.hypot(dx, dy) > threshold;
}

/** Whether the box has anything to pan to, in either direction. */
export function canPan(box) {
    return box.scrollWidth > box.clientWidth || box.scrollHeight > box.clientHeight;
}
